import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

import Button from "@/shared/components/Button/Button";
import SharePopup from "@/components/SharePopup/SharePopup";
import SubscriptionSwitcher from "./SubscriptionSwitcher";
import { getShareData } from "./Subscriptions.utils";
import { useSubscriptionsContext } from "@/contexts/subscriptions";
import { getSubsAddUrl, getSubsEditUrl } from "@/shared/utils/url/url";
import useMediaQuery, { MEDIA_QUERIES } from "@/shared/hooks/useMediaQuery";
import IconView from "@/shared/components/IconView/IconView";
import { SubscriptionsDatePicker } from "@/components/subscriptions/SubscriptionsDatePicker";
import DateSwitcher from "@/shared/components/DateSwitcher/dateSwitcher/DateSwitcher";
import { formatServerDateToJSDate } from "@/shared/utils/date";

import EgwWebFont from "src/assets/EgwWebFont";

import styles from "./subscriptions.module.scss";

const DropDown = dynamic(() => import("@/shared/components/DropDown/DropDown"), {
  ssr: false,
});

const SubsReadTopPanel = ({ currentSub, onDateChange, openReaderHandler, ...restProps }) => {
  const { t } = useTranslation();
  const { subscriptions, date } = useSubscriptionsContext();
  const isMobile = useMediaQuery(MEDIA_QUERIES.untilTablet);
  const shareData = getShareData(currentSub);
  const threeDotsIcons = (
    <>
      <SubscriptionSwitcher {...restProps} />
      <SharePopup
        {...shareData}
        renderButton={({ show, onClick }) => {
          return (
            <IconView
              icon={EgwWebFont.share}
              className={styles["subs-share-icon"]}
              active={show}
              onClick={onClick}
            />
          );
        }}
      />
    </>
  );

  return (
    <div className={classNames(styles["subs-top-panel"], styles["subs-reader-top-panel"])}>
      {isMobile && (
        <DropDown
          button={({ show, onClick }) => {
            return (
              <Button active={show} paddingLess onClick={onClick}>
                <IconView icon={EgwWebFont["dots-vertical"]} />
              </Button>
            );
          }}
          body={<div className={styles["three-dots-popup"]}>{threeDotsIcons}</div>}
        />
      )}

      <DateSwitcher
        SubscriptionsDatePicker={SubscriptionsDatePicker}
        date={formatServerDateToJSDate(date)}
        onDateChange={onDateChange}
      />

      {!isMobile && threeDotsIcons}

      <div className={styles["subs-top-panel-actions"]}>
        <Button
          rootTagName={"a"}
          href={`${getSubsEditUrl()}/edit/${currentSub?.subscription}`}
          target="_blank"
          title={t("subscriptions_edit")}
          className={styles["subs-top-panel-action"]}
          disabled={!subscriptions?.length}>
          <IconView icon={EgwWebFont.edit} />
        </Button>
        <Button
          onClick={openReaderHandler}
          href={getSubsAddUrl()}
          target="_blank"
          title={t("read")}
          className={styles["subs-top-panel-action"]}>
          <IconView icon={EgwWebFont.read} />
        </Button>
      </div>
    </div>
  );
};

SubsReadTopPanel.propTypes = {
  currentSub: PropTypes.object,
  onDateChange: PropTypes.func,
  renderActions: PropTypes.func,
  openReaderHandler: PropTypes.func,
};

export default SubsReadTopPanel;
